import { useEffect, useRef } from 'react';

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  radius: number;
  color: string;
}

export default function About() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const particlesRef = useRef<Particle[]>([]);
  const animationRef = useRef<number>(0);

  const stats = [
    { value: '100%', label: 'Eficiencia Digital' },
    { value: '24/7', label: 'Soporte Técnico' },
    { value: '+5', label: 'Productos Propios' },
    { value: '0', label: 'Tareas Zombi' }
  ];

  // Sistema de Partículas idéntico al Carrusel
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const handleResize = () => {
      canvas.width = window.innerWidth;
      canvas.height = canvas.parentElement?.offsetHeight || 800;
    };
    handleResize();
    window.addEventListener('resize', handleResize);

    const colors = ['#00C2FF', '#1A73E8', '#ffffff'];
    particlesRef.current = Array.from({ length: 30 }, () => ({
      x: Math.random() * canvas.width,
      y: Math.random() * canvas.height,
      vx: (Math.random() - 0.5) * 0.3,
      vy: (Math.random() - 0.5) * 0.3,
      radius: Math.random() * 1.5 + 0.5,
      color: colors[Math.floor(Math.random() * colors.length)]
    }));

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      particlesRef.current.forEach((p, i) => {
        p.x = (p.x + p.vx + canvas.width) % canvas.width;
        p.y = (p.y + p.vy + canvas.height) % canvas.height;
        
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.radius, 0, Math.PI * 2);
        ctx.fillStyle = p.color;
        ctx.globalAlpha = 0.2; // Opacidad baja para no tapar el texto
        ctx.fill();

        particlesRef.current.slice(i + 1).forEach(p2 => {
          const dist = Math.hypot(p.x - p2.x, p.y - p2.y);
          if (dist < 180) {
            ctx.beginPath();
            ctx.moveTo(p.x, p.y);
            ctx.lineTo(p2.x, p2.y);
            ctx.strokeStyle = p.color;
            ctx.globalAlpha = (1 - dist / 180) * 0.05;
            ctx.stroke();
          }
        });
      });
      animationRef.current = requestAnimationFrame(draw);
    };
    draw();

    return () => {
      window.removeEventListener('resize', handleResize);
      cancelAnimationFrame(animationRef.current);
    };
  }, []);

  const scrollToTeam = () => {
    const teamSection = document.getElementById('team');
    if (teamSection) teamSection.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section id="about" className="py-16 sm:py-24 lg:py-32 bg-[#050608] relative overflow-hidden">
      {/* Canvas de Partículas */}
      <canvas ref={canvasRef} className="absolute inset-0 pointer-events-none z-0" />
      
      {/* Luces de ambiente */}
      <div className="absolute top-1/4 left-0 w-[300px] sm:w-[500px] h-[300px] sm:h-[500px] bg-[#00C2FF]/5 blur-[120px] rounded-full pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          
          <div className="space-y-6 sm:space-y-8">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-white/5 border border-white/10 rounded-full backdrop-blur-md">
              <span className="w-2 h-2 rounded-full bg-[#00C2FF] shadow-[0_0_10px_#00C2FF]"></span>
              <span className="text-white text-xs font-bold uppercase tracking-widest">Nuestra Esencia</span>
            </div>

            <h2 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-white tracking-tighter leading-[1.1]">
              Ingeniería con <br /> 
              <span className="text-[#00C2FF]">Propósito.</span>
            </h2>

            <p className="text-base sm:text-lg lg:text-xl text-white/90 leading-relaxed max-w-xl font-medium">
              En <span className="text-white font-bold border-b border-[#00C2FF]">CodeLiance</span> Creamos soluciones digitales a medida que impulsan el crecimiento empresarial. En Codeliance diseñamos y desarrollamos sistemas personalizados, aplicaciones web, móviles y soluciones basadas en inteligencia artificial, enfocadas en optimizar procesos y resolver problemas reales en empresas de Colombia y Latinoamérica.
            </p>

            <div className="grid grid-cols-2 gap-6 sm:gap-10 pt-8 sm:pt-10 border-t border-white/10">
              {stats.map((stat, index) => (
                <div key={index}>
                  <div className="text-3xl sm:text-4xl font-black text-white">{stat.value}</div>
                  <div className="text-[10px] sm:text-xs font-bold text-white/50 uppercase tracking-[0.2em] mt-2">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="relative grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
            <div className="space-y-4 sm:space-y-6">
              <div className="p-6 sm:p-8 rounded-[2rem] sm:rounded-[2.5rem] bg-[#0d1117]/80 backdrop-blur-sm border border-white/10 hover:border-[#00C2FF]/40 transition-all shadow-2xl">
                <i className="ri-rocket-line text-3xl sm:text-4xl text-[#00C2FF] mb-4 sm:mb-6 block"></i>
                <h4 className="text-lg sm:text-xl font-bold text-white mb-2 sm:mb-3">Escalabilidad</h4>
                <p className="text-sm text-white/60">Arquitecturas para crecimiento exponencial.</p>
              </div>
              
              <div className="p-6 sm:p-8 rounded-[2rem] sm:rounded-[2.5rem] bg-[#0d1117]/80 backdrop-blur-sm border border-white/10 hover:border-[#1A73E8]/40 transition-all">
                <i className="ri-shield-check-line text-3xl sm:text-4xl text-[#1A73E8] mb-4 sm:mb-6 block"></i>
                <h4 className="text-lg sm:text-xl font-bold text-white mb-2 sm:mb-3">Seguridad</h4>
                <p className="text-sm text-white/60">Protección bajo estándares empresariales.</p>
              </div>
            </div>

            <div className="space-y-4 sm:space-y-6 sm:mt-12">
              <button 
                onClick={scrollToTeam}
                className="w-full text-left p-6 sm:p-8 rounded-[2rem] sm:rounded-[2.5rem] bg-white hover:bg-[#00C2FF] text-black hover:text-white transition-all duration-500 group shadow-2xl"
              >
                <i className="ri-team-line text-3xl sm:text-4xl mb-6 sm:mb-8 block transition-transform group-hover:scale-110"></i>
                <h4 className="text-xl sm:text-2xl font-black mb-2 uppercase tracking-tighter">Equipo</h4>
                <p className="text-sm font-medium opacity-80 mb-4 sm:mb-6">Talento senior dedicado a tu visión.</p>
                <span className="inline-flex items-center gap-2 text-[10px] font-black uppercase tracking-widest border-t border-current pt-4 w-full">
                  Ver Integrantes <i className="ri-arrow-right-line"></i>
                </span>
              </button>

              <div className="p-6 sm:p-8 rounded-[2rem] sm:rounded-[2.5rem] bg-[#0d1117]/80 backdrop-blur-sm border border-white/10 hover:border-[#1A73E8]/40 transition-all">
                <i className="ri-line-chart-line text-3xl sm:text-4xl text-[#1A73E8] mb-4 sm:mb-6 block"></i>
                <h4 className="text-lg sm:text-xl font-bold text-white mb-2 sm:mb-3">Impacto</h4>
                <p className="text-sm text-white/60">Resultados tangibles y medición de eficiencia.</p>
              </div>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
}